import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Image, Alert, Modal } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import OnboardingLayout from '../OnboardingLayout';
import AnimatedStep from './AnimatedStep';
import axios from '../../../api/axios';
import { useSelector, useDispatch } from 'react-redux';
import { updateFormData } from '../../../redux/onboarding/onboardingSlice'; 
import endpoints from '../../../api/endpoints';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import getCroppedImg from '../../../features/cropImage';
import '../../../styles/Onboarding.css';
import { updateUserProfile, saveProfileImage } from '../../../services/UserService';

const MAX_FILE_SIZE_MB = 5;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

const StepProfileOptional = ({ data, setData, setStepValidity }) => {

  const { user} = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(null);
  
  // Handle Input changes
  const handleInputChange = (field) => (e) => {
    const newData = {
      ...data,
      [field]: e.target.value,
    };
    setData(newData);
    dispatch(updateFormData(newData));
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError(null);
    setSubmitted(false);

    if (rejectedFiles.length > 0) {
      toast.error("Ungültiger Dateityp oder Größe überschritten (max. 5MB).");
      return;
    }

    const file = acceptedFiles[0];
    const reader = new FileReader();;

    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropper(true);
    };

    reader.readAsDataURL(file);
  }, []);

  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'application/pdf'
  ];

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: ALLOWED_TYPES.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    maxSize: MAX_FILE_SIZE_MB * 1024 * 1024,
  });

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const applyCrop = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'profile.jpg', { type: croppedBlob.type });
      setProfilePhoto(croppedFile);

      const newData = { ...data, profilePhoto: croppedFile };
      setData(newData);
      dispatch(updateFormData(newData));
      
      setData((prev) => ({ ...prev, profilePhoto: croppedFile }));
      setShowCropper(false);
      //toast.success("Profilbild zugeschnitten");
    } catch (err) {
      console.error(err);
      toast.error("Fehler beim Zuschneiden");
    }
  };
  
  // Automatically set step validity based on conditions
  useEffect(() => {
    const isFormValid = profilePhoto && data.privacyAccepted;
    dispatch(setStepValidity(isFormValid));

    console.log("StepProfileOptional isFormValid", isFormValid)
  }, [profilePhoto, data.privacyAccepted, dispatch]);
  
  const educationOptions = [
    'Schule',
    'Ausbildung',
    'Studium',
    'Master',
    'Promotion',
  ];

  const fields = [
    { name: 'bio', label: 'Short Bio', sub: '(Optional)', as: 'textarea', rows: 3 },
    { name: 'proficiency', label: 'Beruf', sub: '(Optional)' },
    { name: 'education', label: 'Bildung', sub: '(Optional)' }
  ];

  return (
    <OnboardingLayout>
      <ToastContainer position="top-center" />
      <AnimatedStep>
        <Card className="p-4 shadow-sm">
          {/*fields.map(({ label, sub, name, ...rest }) => (
            <Form.Group key={name} className="mb-3">
              <Form.Label className='text-primary'>
                {label} <span className="text-secondary">{sub}</span>
              </Form.Label>
              <Form.Control
                {...rest}
                value={data[name] || ''}
                onChange={handleInputChange(name)}
              />
            </Form.Group>
          ))*/}

          <Form.Group className="mb-3">
            <Form.Label className='text-primary'>
              Bio <span className="text-secondary">(Optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={data.bio || ''}
              onChange={handleInputChange('bio')}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='text-primary'>
              Jobtitel <span className="text-secondary">(Optional)</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={data.jobTitle || ''}
              onChange={handleInputChange('jobTitle')}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='text-primary'>
              Bildungsniveau <span className="text-secondary">(Optional)</span>
            </Form.Label>
            <Form.Select
              value={data.educationLevel || ''}
              onChange={handleInputChange('educationLevel')}
            >
              <option value="">-- Auswahl treffen --</option>
              {educationOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className='text-primary'>
              Profilbild <span className="text-secondary">(Pflichtfeld! Ohne Bild kein Match!)</span>
            </Form.Label>

            <div
              {...getRootProps()}
              className={`dropzone border p-3 rounded text-center ${isDragActive ? 'bg-light' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <input {...getInputProps()} />
              {isDragActive
                ? <p>Datei hier ablegen...</p>
                : <p>Klicke oder ziehe dein Bild hierher (JPG/PNG, max. 5MB)</p>}
            </div>

            {profilePhoto && (
              <div className="preview-wrapper mt-3 text-center">
                <img
                  src={URL.createObjectURL(profilePhoto)}
                  alt="Profilvorschau"
                  className="img-thumbnail rounded-circle"
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    border: '2px solid #007bff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  }}
                />
                <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                  Vorschau deines Profilbildes
                </div>
              </div>
            )}
            <Form.Check
              type="checkbox"
              label={<>
                Ich stimme zu, dass mein Profilbild gespeichert und anderen Nutzern angezeigt wird.
                <Button variant="link" size="sm" onClick={() => setShowPrivacy(true)}>(Lesen)</Button>
              </>}
              value={data.privacyAccepted}
              checked={data.privacyAccepted || false}
              onChange={(e) => setData({ ...data, privacyAccepted: e.target.checked })}
              //isInvalid={!!errors.privacyAccepted}
            />
            {profilePhoto instanceof File && !data.privacyAccepted && <div className="text-danger">Sie müssen die Datenschützerklärung stimmen ein Profilebild hoch zu laden!</div>}
          </Form.Group>
        </Card>
      </AnimatedStep>

      {/* Privacy Modal */}
      <Modal show={showPrivacy} onHide={() => setShowPrivacy(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Datenschutzerklärung</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* This can later load from a JSON file or backend */}
          <p>[Hier erscheinen die Datenschutzerklärungs-Inhalte]</p>
        </Modal.Body>
      </Modal>
      
      {showCropper && (
        <div className="cropper-modal">
          <div className="cropper-inner">
            <h5 className="mb-3 text-center">Profilbild zuschneiden</h5>
            <div style={{ position: 'relative', width: '100%', height: 300 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="d-flex justify-content-between mt-3">
              <Button variant="secondary" onClick={() => setShowCropper(false)}>Abbrechen</Button>
              <Button variant="primary" onClick={applyCrop}>Zuschneiden & Bestätigen</Button>
            </div>
          </div>
        </div>
      )}

    </OnboardingLayout>
  );
};

export default StepProfileOptional;